<template>
  <div
    v-if="nonSambaContent || vastTagUrl || adContent || isLoadingAd || renderedAds.length > 0"
    class="ads"
  >
    <div class="placeholder">
      <div :id="trackingId" ref="adContainer" class="ad-container">
        <div v-if="vastTagUrl || isLoadingAd" class="samba-ad-wrapper">
          <div v-if="isLoadingAd" class="player-state-overlay loading-state">
            <div class="loading-spinner" />
          </div>

          <samba-vast-player
            v-if="vastTagUrl"
            :key="`${bannerId}-${adKey}`"
            class="samba-vast-player"
            :vast-tag-url="vastTagUrl"
            :player-id="bannerId"
            :width="width"
            :height="height"
            :allow-skip="skipEnabled"
            :skip-delay="skipDelay"
            :ad-type="type"
            @ad-started="onSambaAdStarted"
            @ad-completed="onSambaAdCompleted"
            @ad-impression="onSambaAdImpression"
            @ad-error="onSambaAdError"
            @ad-skipped="onSambaAdSkipped"
          />
        </div>

        <div
          v-if="nonSambaContent"
          :id="bannerId + '_nonsamba'"
          class="non-samba-ad-wrapper"
          v-html="nonSambaContent"
        />

        <div
          v-if="adContent && !vastTagUrl && !nonSambaContent"
          :id="bannerId"
          v-html="adContent"
        />

        <div
          v-for="(ad, index) in renderedAds"
          :id="`rendered-ad-${index}`"
          :key="`ad-${index}`"
          class="rendered-ad-wrapper"
        >
          <div
            v-if="ad.type === 'html'"
            :id="`${bannerId}-${index}`"
            class="ad-content"
            v-html="ad.content"
          />
          <div v-else-if="ad.type === 'samba'" class="samba-ad-wrapper-queue">
            <samba-vast-player
              :key="`samba-${index}-${adKey}`"
              class="samba-vast-player"
              :vast-tag-url="ad.vastUrl"
              :player-id="`${bannerId}-samba-${index}`"
              :width="ad.width || '100%'"
              :height="ad.height || 'auto'"
              :allow-skip="skipEnabled"
              :skip-delay="skipDelay"
              :ad-type="type"
              @ad-started="onSambaAdStarted"
              @ad-completed="onSambaAdCompleted"
              @ad-impression="onSambaAdImpression"
              @ad-error="onSambaAdError"
              @ad-skipped="onSambaAdSkipped"
            />
          </div>
        </div>
      </div>
      <div ref="scriptContainer" />
    </div>
  </div>
</template>

<script>
  import SambaVastPlayer from './SambaVastPlayer.vue'
  import debugLogger from '@/utils/DebugLoggerSDK'

  const logAdsEvent = async (message, data = {}, level = 'info') => {
    const logMethod =
      typeof debugLogger[level] === 'function' ? debugLogger[level] : debugLogger.info
    logMethod.call(debugLogger, message, data)
  }

  export default {
    components: {
      SambaVastPlayer,
    },

    props: {
      campaignId: {
        type: [String, Number],
        required: true,
      },
      position: {
        type: String,
        required: true,
      },
      zoneId: {
        type: [String, Number],
        required: true,
      },
      type: {
        type: String,
        required: true,
      },
      skipEnabled: {
        type: Boolean,
        default: false,
      },
      skipDelay: {
        type: Number,
        default: 15,
      },
    },

    data() {
      return {
        adKey: 1,
        adContent: '',
        api: '/index.php?controller=ads&action=show-ad',
        scriptContent: null,
        isScriptInjected: false,
        firstAdScriptInjected: false,
        injectedAdIndices: new Set(),
        embedDelay: 0,
        trackingSent: false,
        nonSambaContent: '',
        vastTagUrl: '',
        width: '100%',
        height: 'auto',
        hasError: false,
        isLoadingAd: false,
        hasInitialized: false,
        errorTimerInterval: null,
        brokenAdTimeout: null,
        brokenAdCountdownInterval: null,
        isBrokenAd: false,
        adsQueue: [],
        currentAdIndex: 0,
        allAdsRendered: false,
        renderedAds: [],
        firstAdInjected: false,
      }
    },

    computed: {
      trackingId() {
        let id
        if (this.position.includes('interstitial')) {
          id = 'interstitialzone_position_post_interstitialadframe'
        } else {
          const position = this.position.replace('zone_position_post_', '')
          id = `postzone_position_post_${position}adframe`
        }

        return `${id}_${this._uid}`
      },
      bannerId() {
        return `banner_${this.zoneId}_${this.position}_${this._uid}`
      },
      embedId() {
        return `embed_${this.zoneId}_${this.position}_${Date.now()}`
      },
    },

    watch: {
      $route(to, from) {
        logAdsEvent('[ADS] watch: $route changed', {
          fromPath: from.path,
          toPath: to.path,
          pathsMatch: to.path === from.path,
        })

        if (to.path !== from.path) {
          logAdsEvent('[ADS] watch: $route - paths differ, resetting Samba flag', {
            fromPath: from.path,
            toPath: to.path,
          })
          this.resetSambaFlagForNewPage()
        }
      },
    },

    mounted() {
      logAdsEvent('[ADS] mounted - START', {
        timestamp: new Date().toISOString(),
        hasInitialized: this.hasInitialized,
        campaignId: this.campaignId,
        zoneId: this.zoneId,
        position: this.position,
        type: this.type,
        skipEnabled: this.skipEnabled,
        skipDelay: this.skipDelay,
        userAgent: navigator.userAgent,
        url: window.location.href,
        viewport: {
          width: window.innerWidth,
          height: window.innerHeight,
        },
      })

      if (this.hasInitialized) {
        logAdsEvent('[ADS] mounted - already initialized, returning', {})
        return
      }

      this.hasInitialized = true

      logAdsEvent('[ADS] mounted - calling suppressEmbedlyErrors', {})
      this.suppressEmbedlyErrors()

      logAdsEvent('[ADS] mounted - setting up embedly reinitializer', {})
      if (!window.embedlyReinitInterval) {
        window.embedlyReinitInterval = setInterval(() => {
          if (window.embedly && typeof window.embedly === 'function') {
            try {
              const cards = document.querySelectorAll('.embedly-card')
              if (cards.length > 0) {
                window.embedly('card', '.embedly-card')
              }
            } catch (e) {}
          }
        }, 500)
      }

      logAdsEvent('[ADS] mounted - calling resetSambaFlagForNewPage', {})
      this.resetSambaFlagForNewPage()

      logAdsEvent('[ADS] mounted - adding hashchange listener', {})
      window.addEventListener('hashchange', this.handleHashChange)

      logAdsEvent('[ADS] mounted - calling waitForPreviousAd', {})
      this.waitForPreviousAd()

      logAdsEvent('[ADS] mounted - END', {
        hasInitialized: this.hasInitialized,
      })
    },

    beforeDestroy() {
      logAdsEvent('[ADS] beforeDestroy - START', {
        hasErrorTimerInterval: !!this.errorTimerInterval,
        isScriptInjected: this.isScriptInjected,
        hasError: this.hasError,
        isLoadingAd: this.isLoadingAd,
        vastTagUrl: this.vastTagUrl,
        hasAdContent: !!this.adContent,
        hasNonSambaContent: !!this.nonSambaContent,
      })

      logAdsEvent('[ADS] beforeDestroy - removing hashchange listener', {})
      window.removeEventListener('hashchange', this.handleHashChange)

      if (window.parent && window.parent.onscroll) {
        logAdsEvent('[ADS] beforeDestroy - clearing parent onscroll', {})
        window.parent.onscroll = null
      }

      if (this.$refs.scriptContainer) {
        logAdsEvent('[ADS] beforeDestroy - clearing script container', {})
        this.$refs.scriptContainer.innerHTML = ''
      }

      if (this.errorTimerInterval) {
        logAdsEvent('[ADS] beforeDestroy - clearing error timer interval', {})
        clearInterval(this.errorTimerInterval)
        this.errorTimerInterval = null
      }

      if (this.brokenAdTimeout) {
        logAdsEvent('[ADS] beforeDestroy - clearing broken ad timeout', {})
        clearTimeout(this.brokenAdTimeout)
        this.brokenAdTimeout = null
      }

      if (this.brokenAdCountdownInterval) {
        logAdsEvent('[ADS] beforeDestroy - clearing broken ad countdown interval', {})
        clearInterval(this.brokenAdCountdownInterval)
        this.brokenAdCountdownInterval = null
      }

      if (window.AD_CONFIG) {
        logAdsEvent('[ADS] beforeDestroy - deleting AD_CONFIG', {})
        delete window.AD_CONFIG
      }
      if (window.tracking) {
        logAdsEvent('[ADS] beforeDestroy - deleting tracking', {})
        delete window.tracking
      }
      this.isScriptInjected = false

      logAdsEvent('[ADS] beforeDestroy - clearing renderedAds and firstAdInjected', {
        renderedAdsCount: this.renderedAds.length,
        firstAdInjected: this.firstAdInjected,
        firstAdScriptInjected: this.firstAdScriptInjected,
        injectedAdIndicesSize: this.injectedAdIndices.size,
      })
      this.renderedAds = []
      this.firstAdInjected = false
      this.firstAdScriptInjected = false
      this.injectedAdIndices.clear()

      logAdsEvent('[ADS] beforeDestroy - END', {})
    },

    methods: {
      handleHashChange() {
        logAdsEvent('[ADS] handleHashChange - START', {
          currentHash: window.location.hash,
          currentPath: window.location.pathname,
        })
        this.resetSambaFlagForNewPage()
        logAdsEvent('[ADS] handleHashChange - END', {})
      },

      resetSambaFlagForNewPage() {
        const currentPath =
          this.$route?.path ||
          this.$route?.fullPath ||
          window.location.hash ||
          window.location.pathname

        logAdsEvent('[ADS] resetSambaFlagForNewPage - START', {
          currentPath,
          previousPath: window.CURRENT_PAGE_PATH,
          sambaAdShown: window.SAMBA_AD_SHOWN_ON_PAGE,
          pathsMatch: window.CURRENT_PAGE_PATH === currentPath,
          queueBefore: {
            currentAd: window.AD_LOADING_QUEUE?.currentAd,
            queueLength: window.AD_LOADING_QUEUE?.queue?.length,
            loadedAdsCount: window.AD_LOADING_QUEUE?.loadedAds?.size,
          },
        })

        if (!window.CURRENT_PAGE_PATH || window.CURRENT_PAGE_PATH !== currentPath) {
          logAdsEvent('[ADS] resetSambaFlagForNewPage - resetting flag and queue', {
            previousPath: window.CURRENT_PAGE_PATH,
            newPath: currentPath,
          })
          window.SAMBA_AD_SHOWN_ON_PAGE = false
          window.CURRENT_PAGE_PATH = currentPath
          window.AD_LOADING_QUEUE = null
        }

        logAdsEvent('[ADS] resetSambaFlagForNewPage - END', {
          currentPath: window.CURRENT_PAGE_PATH,
          sambaAdShown: window.SAMBA_AD_SHOWN_ON_PAGE,
          queueAfter: {
            currentAd: window.AD_LOADING_QUEUE?.currentAd,
            queueLength: window.AD_LOADING_QUEUE?.queue?.length,
            loadedAdsCount: window.AD_LOADING_QUEUE?.loadedAds?.size,
          },
        })
      },

      async waitForPreviousAd() {
        const adId = `${this.zoneId}_${this.position}`
        const initKey = `samba_ads_init_${adId}`

        logAdsEvent('[ADS] waitForPreviousAd - START', {
          adId,
          initKey,
          hasInitKey: !!window[initKey],
          isLoadingAd: this.isLoadingAd,
          hasAdLoadingQueue: !!window.AD_LOADING_QUEUE,
          queueCurrentAd: window.AD_LOADING_QUEUE?.currentAd,
          queueLength: window.AD_LOADING_QUEUE?.queue?.length,
          loadedAdsCount: window.AD_LOADING_QUEUE?.loadedAds?.size,
        })

        if (window[initKey]) {
          logAdsEvent('[ADS] waitForPreviousAd - already initialized, returning', {
            adId,
            initKey,
          })
          return
        }

        window[initKey] = true

        if (this.isLoadingAd) {
          logAdsEvent('[ADS] waitForPreviousAd - already loading ad, returning', {
            adId,
            isLoadingAd: this.isLoadingAd,
          })
          window[initKey] = false
          return
        }

        if (!window.AD_LOADING_QUEUE) {
          logAdsEvent('[ADS] waitForPreviousAd - creating AD_LOADING_QUEUE', {})
          window.AD_LOADING_QUEUE = {
            currentAd: null,
            queue: [],
            loadedAds: new Set(),
          }
        }

        if (window.AD_LOADING_QUEUE.loadedAds.has(adId)) {
          logAdsEvent('[ADS] waitForPreviousAd - ad already loaded, marking complete', {
            adId,
          })
          window[initKey] = false
          this.markAdComplete()
          return
        }

        if (
          window.AD_LOADING_QUEUE.queue.includes(adId) &&
          window.AD_LOADING_QUEUE.currentAd !== adId
        ) {
          logAdsEvent('[ADS] waitForPreviousAd - ad in queue but not current, returning', {
            adId,
            currentAd: window.AD_LOADING_QUEUE.currentAd,
            queue: window.AD_LOADING_QUEUE.queue,
          })
          window[initKey] = false
          return
        }

        if (!window.AD_LOADING_QUEUE.queue.includes(adId)) {
          logAdsEvent('[ADS] waitForPreviousAd - adding ad to queue', {
            adId,
            queueBefore: [...window.AD_LOADING_QUEUE.queue],
          })
          window.AD_LOADING_QUEUE.queue.push(adId)
        }

        logAdsEvent('[ADS] waitForPreviousAd - waiting for turn', {
          adId,
          queue: window.AD_LOADING_QUEUE.queue,
          currentAd: window.AD_LOADING_QUEUE.currentAd,
        })
        await this.waitForTurn(adId)

        logAdsEvent('[ADS] waitForPreviousAd - turn received, loading ad', {
          adId,
        })
        this.loadAd()

        logAdsEvent('[ADS] waitForPreviousAd - END', {
          adId,
        })
      },

      async waitForTurn(adId) {
        logAdsEvent('[ADS] waitForTurn - START', {
          adId,
          currentAd: window.AD_LOADING_QUEUE?.currentAd,
          queue: window.AD_LOADING_QUEUE?.queue,
        })

        return new Promise((resolve) => {
          let attempts = 0
          const maxAttempts = 100
          const checkTurn = () => {
            attempts++

            if (!window.AD_LOADING_QUEUE.currentAd) {
              logAdsEvent('[ADS] waitForTurn - no current ad, taking turn', {
                adId,
                attempts,
              })
              window.AD_LOADING_QUEUE.currentAd = adId
              resolve()
              return
            }

            const currentIndex = window.AD_LOADING_QUEUE.queue.indexOf(adId)
            const currentAdIndex = window.AD_LOADING_QUEUE.queue.indexOf(
              window.AD_LOADING_QUEUE.currentAd,
            )
            const currentAdLoaded = window.AD_LOADING_QUEUE.loadedAds.has(
              window.AD_LOADING_QUEUE.currentAd,
            )

            if (currentIndex === currentAdIndex + 1 && currentAdLoaded) {
              window.AD_LOADING_QUEUE.currentAd = adId
              resolve()
              return
            }

            // Timeout: if we've been waiting too long and the current ad is from a different page, skip it
            if (attempts >= maxAttempts) {
              window.AD_LOADING_QUEUE.currentAd = adId
              resolve()
              return
            }

            setTimeout(checkTurn, 100)
          }
          checkTurn()
        })
      },

      loadAd() {
        logAdsEvent('[ADS] loadAd - START', {
          isLoadingAd: this.isLoadingAd,
          zoneId: this.zoneId,
          position: this.position,
          campaignId: this.campaignId,
          type: this.type,
          sambaAdShown: window.SAMBA_AD_SHOWN_ON_PAGE,
        })

        if (this.isLoadingAd) {
          logAdsEvent('[ADS] loadAd - already loading, returning', {
            isLoadingAd: this.isLoadingAd,
          })
          return
        }

        const adId = `${this.zoneId}_${this.position}`
        if (window.AD_LOADING_QUEUE?.loadedAds?.has(adId)) {
          logAdsEvent('[ADS] loadAd - ad already in loadedAds, returning', {
            adId,
          })
          return
        }

        this.renderedAds = []
        this.firstAdInjected = false
        this.firstAdScriptInjected = false
        this.injectedAdIndices.clear()
        this.isLoadingAd = true
        logAdsEvent(
          '[ADS] loadAd - set isLoadingAd=true and reset renderedAds/firstAdInjected/scriptTracking',
          {
            isLoadingAd: this.isLoadingAd,
            renderedAdsCount: this.renderedAds.length,
            firstAdInjected: this.firstAdInjected,
            firstAdScriptInjected: this.firstAdScriptInjected,
            injectedAdIndicesSize: this.injectedAdIndices.size,
          },
        )

        let campaignId = this.campaignId
        if (!campaignId) {
          logAdsEvent('[ADS] loadAd - no campaignId, getting from store', {
            hasStore: !!this.$store,
            hasPostLogin: !!this.$store?.getters?.postLogin,
            postLoginId: this.$store?.getters?.postLogin?.id,
          })
          campaignId = this.$store.getters.postLogin?.id
        }

        logAdsEvent('[ADS] loadAd - checking parameters', {
          campaignId,
          zoneId: this.zoneId,
          position: this.position,
          type: this.type,
          allParamsPresent: !!(campaignId && this.zoneId && this.position && this.type),
        })

        if (campaignId && this.zoneId && this.position && this.type) {
          logAdsEvent('[ADS] loadAd - all params present, fetching ad data', {
            campaignId,
            zoneId: this.zoneId,
            position: this.position,
            type: this.type,
          })
          this.fetchAdData(campaignId)
        } else {
          logAdsEvent('[ADS] loadAd - missing params, marking complete', {
            campaignId,
            zoneId: this.zoneId,
            position: this.position,
            type: this.type,
          })
          this.isLoadingAd = false
          this.markAdComplete()
        }

        logAdsEvent('[ADS] loadAd - END', {
          isLoadingAd: this.isLoadingAd,
        })
      },

      fetchAdData(campaignId) {
        const url = `${this.api}&key=${campaignId}&zone=${this.zoneId}&tile=${this.position}&type=${this.type}`

        logAdsEvent('[ADS] fetchAdData - START', {
          campaignId,
          zoneId: this.zoneId,
          position: this.position,
          type: this.type,
          url,
          isLoadingAd: this.isLoadingAd,
          timestamp: new Date().toISOString(),
        })

        axios
          .get(url)
          .then((res) => {
            logAdsEvent('[ADS] fetchAdData - response received', {
              status: res.status,
              hasAdsArray: !!res.data?.ads,
              adsCount: res.data?.ads?.length,
              hasOutput: !!res.data?.output,
              timestamp: new Date().toISOString(),
            })

            this.isLoadingAd = false

            // Handle new multi-ad response format
            if (res.data && res.data.ads && Array.isArray(res.data.ads)) {
              logAdsEvent('[ADS] fetchAdData - received ads array', {
                adsCount: res.data.ads.length,
                ads: res.data.ads.map((ad) => ({ id: ad.id, type: ad.type })),
              })

              if (res.data.ads.length === 0) {
                logAdsEvent('[ADS] fetchAdData - ads array is empty')
                this.showBrokenAd()
                return
              }

              // Store ads queue and render first one
              this.adsQueue = res.data.ads
              this.currentAdIndex = 0
              this.renderCurrentAd()
              return
            }

            // Legacy output format (for backward compatibility)
            if (res.data && res.data.output) {
              logAdsEvent('[ADS] fetchAdData - legacy output format detected', {
                outputLength: res.data.output.length,
              })

              if (res.data.output.trim() === '') {
                this.showBrokenAd()
                return
              }

              logAdsEvent('[ADS] fetchAdData - parsing Samba ad parameters', {
                hasOutput: !!res.data?.output,
                outputLength: res.data?.output?.length,
              })
              const sambaResult = this.parseSambaAdParameters(res.data.output)

              logAdsEvent('[ADS] fetchAdData - parse result', {
                success: sambaResult.success,
                isSambaAd: sambaResult.isSambaAd,
                hasVastTagUrl: !!sambaResult.vastTagUrl,
                vastTagUrl: sambaResult.vastTagUrl,
                hasError: !!sambaResult.error,
                error: sambaResult.error,
              })

              if (sambaResult.success && sambaResult.isSambaAd) {
                logAdsEvent('[ADS] fetchAdData - handling Samba ad', {
                  vastTagUrl: sambaResult.vastTagUrl,
                  width: sambaResult.width,
                  height: sambaResult.height,
                  hasNonSambaContent: !!sambaResult.nonSambaContent,
                  previousAdKey: this.adKey,
                  sambaAdAlreadyShown: window.SAMBA_AD_SHOWN_ON_PAGE,
                })

                // Check if a Samba ad has already been shown on this page
                const sambaAdShown = window.SAMBA_AD_SHOWN_ON_PAGE || false
                if (sambaAdShown) {
                  logAdsEvent(
                    '[ADS] fetchAdData - Samba ad already shown on page, skipping and marking complete',
                    {
                      sambaAdShown,
                      position: this.position,
                    },
                  )
                  this.isLoadingAd = false
                  this.markAdComplete()
                  return
                }

                this.hasError = false
                this.adKey++
                this.handleSambaAd(res.data.output)
              } else {
                logAdsEvent('[ADS] fetchAdData - handling non-Samba ad', {
                  hasOutput: !!res.data?.output,
                  outputLength: res.data?.output?.length,
                  previousAdKey: this.adKey,
                })
                this.hasError = false
                this.injectAdContent(res.data.output)
                this.adKey++
              }

              logAdsEvent('[ADS] fetchAdData - END (success)', {
                isSambaAd: sambaResult.isSambaAd,
                hasError: this.hasError,
                adKey: this.adKey,
              })
            }
          })
          .catch((error) => {
            logAdsEvent('[ADS] fetchAdData - ERROR', {
              error: error.message,
              errorName: error.name,
              errorCode: error.code,
              errorStack: error.stack,
              status: error.response?.status,
              statusText: error.response?.statusText,
              timestamp: new Date().toISOString(),
            })
            this.isLoadingAd = false
            this.handleAdError(error.message || 'Failed to load ad')
          })
      },

      handleAdError(errorMessage) {
        logAdsEvent('[ADS] handleAdError - START', {
          errorMessage,
          hasAdsQueue: !!this.adsQueue.length,
          currentAdIndex: this.currentAdIndex,
          totalAds: this.adsQueue.length,
          allAdsRendered: this.allAdsRendered,
          hasNonSambaContent: !!this.nonSambaContent,
          hasAdContent: !!this.adContent,
          timestamp: new Date().toISOString(),
        })

        if (this.allAdsRendered && (this.nonSambaContent || this.adContent)) {
          logAdsEvent(
            '[ADS] handleAdError - all ads rendered with valid content, marking complete',
            {
              hasNonSambaContent: !!this.nonSambaContent,
              hasAdContent: !!this.adContent,
            },
          )
          this.markAdComplete()
          return
        }

        // If we have multiple ads in queue, try the next one
        if (this.adsQueue.length > 0) {
          logAdsEvent('[ADS] handleAdError - trying next ad from queue')
          this.tryNextAd()
        } else {
          // No ads in queue, show broken ad UI
          logAdsEvent('[ADS] handleAdError - no ads in queue, showing broken ad')
          this.showBrokenAd()
        }

        logAdsEvent('[ADS] handleAdError - END', {
          currentAdIndex: this.currentAdIndex,
        })
      },

      startErrorTimer() {
        logAdsEvent('[ADS] startErrorTimer - START', {
          hasErrorTimerInterval: !!this.errorTimerInterval,
          zoneId: this.zoneId,
          position: this.position,
        })

        if (this.errorTimerInterval) {
          logAdsEvent('[ADS] startErrorTimer - clearing existing interval', {})
          clearInterval(this.errorTimerInterval)
          this.errorTimerInterval = null
        }

        logAdsEvent('[ADS] startErrorTimer - emitting ad-error-timer-start', {
          zoneId: this.zoneId,
          position: this.position,
          duration: 3,
        })
        this.$emit('ad-error-timer-start', {
          zoneId: this.zoneId,
          position: this.position,
          duration: 3,
        })

        let timer = 3
        logAdsEvent('[ADS] startErrorTimer - starting interval', {
          timer,
          duration: 3,
        })
        this.errorTimerInterval = setInterval(() => {
          timer--

          if (timer % 1 === 0) {
            logAdsEvent('[ADS] startErrorTimer - tick', {
              timer,
              remaining: timer,
            })
          }

          if (timer <= 0) {
            logAdsEvent('[ADS] startErrorTimer - timer expired', {
              timer,
            })
            clearInterval(this.errorTimerInterval)
            this.errorTimerInterval = null

            logAdsEvent('[ADS] startErrorTimer - emitting ad-error', {
              zoneId: this.zoneId,
              position: this.position,
            })
            this.$emit('ad-error', {
              zoneId: this.zoneId,
              position: this.position,
              error: 'Samba server unreachable',
            })

            logAdsEvent('[ADS] startErrorTimer - marking ad complete', {})
            this.markAdComplete()
          }
        }, 1000)

        logAdsEvent('[ADS] startErrorTimer - END', {
          hasErrorTimerInterval: !!this.errorTimerInterval,
        })
      },

      markAdComplete() {
        const adId = `${this.zoneId}_${this.position}`
        const initKey = `samba_ads_init_${adId}`

        logAdsEvent('[ADS] markAdComplete - START', {
          adId,
          initKey,
          isLoadingAd: this.isLoadingAd,
          hasError: this.hasError,
          vastTagUrl: this.vastTagUrl,
          hasVastTagUrl: !!this.vastTagUrl,
          hasAdContent: !!this.adContent,
          hasNonSambaContent: !!this.nonSambaContent,
          hasAdLoadingQueue: !!window.AD_LOADING_QUEUE,
          currentAd: window.AD_LOADING_QUEUE?.currentAd,
          loadedAdsCount: window.AD_LOADING_QUEUE?.loadedAds?.size,
          timestamp: new Date().toISOString(),
        })

        if (window.AD_LOADING_QUEUE) {
          logAdsEvent('[ADS] markAdComplete - updating AD_LOADING_QUEUE', {
            adId,
            previousCurrentAd: window.AD_LOADING_QUEUE.currentAd,
            previousLoadedAdsCount: window.AD_LOADING_QUEUE.loadedAds.size,
          })
          window.AD_LOADING_QUEUE.loadedAds.add(adId)
          window.AD_LOADING_QUEUE.currentAd = null
          logAdsEvent('[ADS] markAdComplete - AD_LOADING_QUEUE updated', {
            currentAd: window.AD_LOADING_QUEUE.currentAd,
            loadedAdsCount: window.AD_LOADING_QUEUE.loadedAds.size,
            adInLoadedAds: window.AD_LOADING_QUEUE.loadedAds.has(adId),
          })
        }

        if (window[initKey]) {
          logAdsEvent('[ADS] markAdComplete - clearing init key', {
            initKey,
          })
          window[initKey] = false
        }

        this.isLoadingAd = false

        const hasValidNonSambaContent = this.nonSambaContent && this.nonSambaContent.trim() !== ''
        const hasValidAdContent = this.adContent && this.adContent.trim() !== ''
        const hasValidVastTagUrl = !!this.vastTagUrl

        const isBroken =
          this.isBrokenAd || (!hasValidAdContent && !hasValidNonSambaContent && !hasValidVastTagUrl)
        this.isBrokenAd = isBroken // Update internal flag

        // 🔥 CRITICAL FIX: Tracking is now a side effect handled by injectScriptForTracking()
        // This method purely signals "content is ready to display"
        logAdsEvent('[ADS] markAdComplete - emitting ad-loaded', {
          zoneId: this.zoneId,
          position: this.position,
          adId,
          isSambaAd: !!this.vastTagUrl,
          type: this.type,
          isBroken: this.isBrokenAd,
          hasValidAdContent,
          hasValidNonSambaContent,
          hasValidVastTagUrl,
        })

        this.$emit('ad-loaded', {
          zoneId: this.zoneId,
          position: this.position,
          adId,
          isSambaAd: !!this.vastTagUrl,
          type: this.type,
          isBrokenAd: this.isBrokenAd, // Include broken ad flag
        })

        logAdsEvent('[ADS] markAdComplete - END', {
          isLoadingAd: this.isLoadingAd,
          hasError: this.hasError,
          adId,
        })
      },

      triggerTracking() {
        const hasEmbed = this.hasEmbedContent(this.adContent)

        if (hasEmbed) {
          this.triggerEmbedTracking()
        } else {
          this.triggerRegularTracking()
        }
      },

      triggerYouTubeTracking() {
        if (this.trackingSent) {
          return
        }

        this.sendManualTracking()

        if (typeof window.tracking === 'function') {
          try {
            window.tracking(this.trackingId)
            this.trackingSent = true
          } catch (error) {
            logAdsEvent('[ADS] Failed to trigger immediate YouTube tracking', {
              error: error.message,
              errorName: error.name,
              errorStack: error.stack,
              trackingId: this.trackingId,
              position: this.position,
            })
          }
        }

        if (typeof window.IntersectionObserver !== 'undefined') {
          const observer = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting && !this.trackingSent) {
                if (typeof window.tracking === 'function') {
                  try {
                    window.tracking(this.trackingId)
                    this.trackingSent = true
                  } catch (error) {
                    logAdsEvent('[ADS] Failed to trigger YouTube tracking function', {
                      error: error.message,
                      errorName: error.name,
                      errorStack: error.stack,
                      trackingId: this.trackingId,
                      position: this.position,
                      isIntersecting: entry.isIntersecting,
                    })
                  }
                }

                if (typeof window.scrollFunction === 'function') {
                  try {
                    window.scrollFunction()
                  } catch (error) {
                    logAdsEvent('[ADS] Failed to trigger YouTube scroll function', {
                      error: error.message,
                      errorName: error.name,
                      errorStack: error.stack,
                      position: this.position,
                    })
                  }
                }
              }
            })
          })

          const element = document.getElementById(this.bannerId)
          if (element) {
            observer.observe(element)
          }
        }

        setTimeout(() => {
          if (!this.trackingSent) {
            if (typeof window.tracking === 'function') {
              try {
                window.tracking(this.trackingId)
                this.trackingSent = true
              } catch (error) {
                logAdsEvent('[ADS] Failed to trigger YouTube tracking (1s)', {
                  error: error.message,
                  errorName: error.name,
                  errorStack: error.stack,
                  trackingId: this.trackingId,
                  position: this.position,
                  delay: '1s',
                })
              }
            }
            this.sendManualTracking()
          }
        }, 1000)

        setTimeout(() => {
          if (!this.trackingSent) {
            if (typeof window.tracking === 'function') {
              try {
                window.tracking(this.trackingId)
                this.trackingSent = true
              } catch (error) {
                logAdsEvent('[ADS] Failed to trigger YouTube tracking (3s)', {
                  error: error.message,
                  errorName: error.name,
                  errorStack: error.stack,
                  trackingId: this.trackingId,
                  position: this.position,
                  delay: '3s',
                })
              }
            }
            this.sendManualTracking()
          }
        }, 3000)

        setTimeout(() => {
          if (!this.trackingSent) {
            if (typeof window.tracking === 'function') {
              try {
                window.tracking(this.trackingId)
                this.trackingSent = true
              } catch (error) {
                logAdsEvent('[ADS] Failed to trigger YouTube tracking (5s)', {
                  error: error.message,
                  errorName: error.name,
                  errorStack: error.stack,
                  trackingId: this.trackingId,
                  position: this.position,
                  delay: '5s',
                })
              }
            }
            this.sendManualTracking()
          }
        }, 5000)

        if (typeof window.scrollFunction === 'function') {
          try {
            window.scrollFunction()
          } catch (error) {
            logAdsEvent('[ADS] Failed to trigger YouTube scroll function', {
              error: error.message,
              errorName: error.name,
              errorStack: error.stack,
              position: this.position,
            })
          }
        }
      },

      sendManualTracking() {
        if (this.trackingSent) {
          return
        }

        try {
          const trackingData = `campaign_id=${this.campaignId}&zone_id=${this.zoneId}&ad_id=${this.zoneId}&position=${this.position}&type=${this.type}&socialUserDeviceInfoId=0`

          fetch('/index.php?controller=ads&action=track-impression', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/x-www-form-urlencoded',
            },
            body: trackingData,
          })
            .then((response) => {
              this.trackingSent = true
            })
            .catch((error) => {
              logAdsEvent('[ADS] Manual tracking failed', {
                error: error.message,
                errorName: error.name,
                errorStack: error.stack,
                position: this.position,
                campaignId: this.campaignId,
                zoneId: this.zoneId,
              })
            })
        } catch (error) {
          logAdsEvent('[ADS] Failed to send manual tracking', {
            error: error.message,
            errorName: error.name,
            errorStack: error.stack,
            position: this.position,
            campaignId: this.campaignId,
            zoneId: this.zoneId,
          })
        }
      },

      triggerEmbedTracking() {
        if (this.trackingSent) {
          return
        }

        this.sendManualTracking()

        if (typeof window.tracking === 'function') {
          try {
            window.tracking(this.trackingId)
            this.trackingSent = true
          } catch (error) {
            logAdsEvent('[ADS] Failed to trigger immediate embed tracking', {
              error: error.message,
              errorName: error.name,
              errorStack: error.stack,
              trackingId: this.trackingId,
              position: this.position,
            })
          }
        }

        if (typeof window.IntersectionObserver !== 'undefined') {
          const observer = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting && !this.trackingSent) {
                if (typeof window.tracking === 'function') {
                  try {
                    window.tracking(this.trackingId)
                    this.trackingSent = true
                  } catch (error) {
                    logAdsEvent('[ADS] Failed to trigger embed tracking function', {
                      error: error.message,
                      errorName: error.name,
                      errorStack: error.stack,
                      trackingId: this.trackingId,
                      position: this.position,
                      isIntersecting: entry.isIntersecting,
                    })
                  }
                }

                if (typeof window.scrollFunction === 'function') {
                  try {
                    window.scrollFunction()
                  } catch (error) {
                    logAdsEvent('[ADS] Failed to trigger embed scroll function', {
                      error: error.message,
                      errorName: error.name,
                      errorStack: error.stack,
                      trackingId: this.trackingId,
                      position: this.position,
                    })
                  }
                }
              }
            })
          })

          const element = document.getElementById(this.bannerId)
          if (element) {
            observer.observe(element)
          }
        }

        setTimeout(() => {
          if (!this.trackingSent && typeof window.tracking === 'function') {
            try {
              window.tracking(this.trackingId)
              this.trackingSent = true
            } catch (error) {
              logAdsEvent('[ADS] Failed to trigger embed tracking (1s)', {
                error: error.message,
                errorName: error.name,
                errorStack: error.stack,
                trackingId: this.trackingId,
                position: this.position,
              })
            }
          }
        }, 1000)

        setTimeout(() => {
          if (!this.trackingSent && typeof window.tracking === 'function') {
            try {
              window.tracking(this.trackingId)
              this.trackingSent = true
            } catch (error) {
              logAdsEvent('[ADS] Failed to trigger embed tracking (3s)', {
                error: error.message,
                errorName: error.name,
                errorStack: error.stack,
                trackingId: this.trackingId,
                position: this.position,
              })
            }
          }
        }, 3000)
      },

      triggerRegularTracking() {
        if (typeof window.tracking === 'function') {
          try {
            window.tracking(this.trackingId)
          } catch (error) {
            logAdsEvent('[ADS] Failed to trigger tracking function', {
              error: error.message,
              errorName: error.name,
              errorStack: error.stack,
              trackingId: this.trackingId,
              position: this.position,
            })
          }
        }

        if (typeof window.scrollFunction === 'function') {
          try {
            window.scrollFunction()
          } catch (error) {
            logAdsEvent('[ADS] Failed to trigger scroll function', {
              error: error.message,
              errorName: error.name,
              errorStack: error.stack,
              position: this.position,
            })
          }
        }

        if (window.AD_CONFIG && typeof window.AD_CONFIG.init === 'function') {
          try {
            window.AD_CONFIG.init()
          } catch (error) {
            logAdsEvent('[ADS] Failed to initialize AD_CONFIG', {
              error: error.message,
              errorName: error.name,
              errorStack: error.stack,
              position: this.position,
            })
          }
        }

        if (window.adTracking && typeof window.adTracking.trackImpression === 'function') {
          try {
            window.adTracking.trackImpression(this.zoneId, this.position)
          } catch (error) {
            logAdsEvent('[ADS] Failed to trigger global ad tracking', {
              error: error.message,
              errorName: error.name,
              errorStack: error.stack,
              zoneId: this.zoneId,
              position: this.position,
            })
          }
        }
      },

      suppressEmbedlyErrors() {
        if (window.EMBEDLY_ERRORS_SUPPRESSED) return
        window.EMBEDLY_ERRORS_SUPPRESSED = true
      },

      hasEmbedContent(htmlContent) {
        return (
          htmlContent.includes('<iframe') ||
          htmlContent.includes('embedly-card') ||
          htmlContent.includes('embedly-embed') ||
          htmlContent.includes('<embed') ||
          htmlContent.includes('class="embedly') ||
          htmlContent.includes('data-embedly')
        )
      },

      injectTrackingScript(tracking) {
        if (!tracking) return

        this.$nextTick(() => {
          try {
            const container = this.$refs.scriptContainer || document.body
            const tempDiv = document.createElement('div')
            tempDiv.innerHTML = tracking
            while (tempDiv.firstChild) {
              container.appendChild(tempDiv.firstChild)
            }
          } catch (e) {
            logAdsEvent(
              '[ADS] injectTrackingScript - failed to inject tracking',
              {
                error: e.message,
              },
              'warn',
            )
          }
        })
      },

      processAdScript(scriptContent) {
        if (!scriptContent) return null

        let processed = scriptContent

        processed = processed.replace(
          /window\.parent\.document\.getElementById\(([^)]+)\)\.offsetTop/g,
          '(window.parent.document.getElementById($1) ? window.parent.document.getElementById($1).offsetTop : 0)',
        )

        processed = processed.replace(/window\.AD_CONFIG\s*=\s*{/g, 'window.AD_CONFIG = {')

        processed = processed.replace(
          /getElementTop\("([^"]+)"\)/g,
          `getElementTop("${this.bannerId}")`,
        )

        processed = processed.replace(/tracking\("([^"]+)"\)/g, `tracking("${this.trackingId}")`)

        processed = processed.replace(
          /scrollFunction\(\)\s*{\s*tracking\("([^"]+)"\);\s*}/g,
          `scrollFunction(){\n                tracking("${this.trackingId}");\n            }`,
        )

        processed = processed.replace(/var tracked=!1;/g, 'var tracked=false;')

        return processed
      },

      injectProcessedScript(scriptContent, adIndex, logContext = 'ad') {
        if (!scriptContent) return

        this.$nextTick(() => {
          setTimeout(() => {
            try {
              logAdsEvent(
                `[ADS] injectProcessedScript - ready to inject script for ${logContext}`,
                {
                  adIndex,
                  scriptLength: scriptContent.length,
                },
              )

              const container = this.$refs.scriptContainer || document.body
              const scriptElement = document.createElement('script')
              scriptElement.textContent = scriptContent
              container.appendChild(scriptElement)

              logAdsEvent(`[ADS] injectProcessedScript - injected script for ${logContext}`, {
                adIndex,
              })
            } catch (e) {
              logAdsEvent(
                `[ADS] injectProcessedScript - failed to inject ${logContext} script`,
                {
                  error: e.message,
                  adIndex,
                },
                'warn',
              )
            }
          }, 300)
        })
      },

      handleAdIndexProgress(requiresVastTag = false) {
        this.currentAdIndex++
        if (this.currentAdIndex < this.adsQueue.length) {
          logAdsEvent('[ADS] handleAdIndexProgress - more ads in queue, rendering next ad', {
            nextAdIndex: this.currentAdIndex,
            totalAds: this.adsQueue.length,
          })
          this.renderCurrentAd()
        } else {
          this.allAdsRendered = true
          if (requiresVastTag && this.vastTagUrl) {
            logAdsEvent('[ADS] handleAdIndexProgress - all ads rendered, waiting for Samba ad', {
              hasVastTagUrl: !!this.vastTagUrl,
            })
          } else if (!requiresVastTag) {
            logAdsEvent('[ADS] handleAdIndexProgress - all ads rendered, marking complete')
            this.markAdComplete()
          }
        }
      },

      renderEmbedContent(htmlContent, embedUrl) {
        if (htmlContent && htmlContent.trim()) {
          return `<div class="embedly-responsive">${htmlContent}</div>`
        }

        if (embedUrl && embedUrl.trim()) {
          this.loadEmbedlyScript()
          return `<div class="embedly-responsive">
           <a href="${embedUrl}" 
              class="embedly-card" 
              data-card-controls="0">
           </a>
         </div>`
        }

        return ''
      },

      loadEmbedlyScript() {
        if (document.getElementById('embedly-platform')) {
          return
        }

        if (!window.embedly) {
          window.embedly =
            window.embedly ||
            function () {
              ;(window.embedly.q = window.embedly.q || []).push(arguments)
            }
        }

        const script = document.createElement('script')
        script.id = 'embedly-platform'
        script.async = true
        const protocol = document.location.protocol === 'https:' ? 'https' : 'http'
        script.src = `${protocol}://cdn.embedly.com/widgets/platform.js`

        const firstScript = document.getElementsByTagName('script')[0]
        if (firstScript && firstScript.parentNode) {
          firstScript.parentNode.insertBefore(script, firstScript)
        } else {
          document.body.appendChild(script)
        }
      },

      renderAdWithContent(ad, isFirstAd, hasMoreAds, htmlContent, embedUrl) {
        const contentToProcess =
          htmlContent || embedUrl ? this.renderEmbedContent(htmlContent, embedUrl) : ad.content

        const temp = document.createElement('div')
        temp.innerHTML = contentToProcess
        const script = temp.querySelector('script')
        let scriptContent = null
        const hasEmbed = this.hasEmbedContent(temp.innerHTML)

        if (script) {
          scriptContent = this.processAdScript(script.textContent)

          if (hasEmbed) {
            const trackingVarName = `tracked_embed_${this.position.replace(/[^a-zA-Z0-9]/g, '_')}_${
              this.renderedAds.length
            }`
            scriptContent += `
             var ${trackingVarName} = false;

             function trackEmbed_${this.renderedAds.length}() {
               if (${trackingVarName}) {
                 return;
               }

               if (typeof tracking === 'function') {
                 tracking("${this.trackingId}");
                 ${trackingVarName} = true;
               }
             }

             trackEmbed_${this.renderedAds.length}();

             if (typeof window.IntersectionObserver !== 'undefined') {
               const observer_${this.renderedAds.length} = new IntersectionObserver(function(entries) {
                 entries.forEach(function(entry) {
                   if (entry.isIntersecting) {
                     trackEmbed_${this.renderedAds.length}();
                   }
                 });
               });

               const element_${this.renderedAds.length} = document.getElementById("${this.bannerId}-${this.renderedAds.length}");
               if (element_${this.renderedAds.length}) {
                 observer_${this.renderedAds.length}.observe(element_${this.renderedAds.length});
               }
             }

             setTimeout(function() {
               trackEmbed_${this.renderedAds.length}();
             }, 1000);

             setTimeout(function() {
               trackEmbed_${this.renderedAds.length}();
             }, 3000);

             window.addEventListener('load', function() {
               trackEmbed_${this.renderedAds.length}();
             });
           `
          }

          script.remove()
        }

        const adIndex = this.renderedAds.length
        const adElementId = `${this.bannerId}-${adIndex}`

        if (isFirstAd && !this.renderedAds.length) {
          this.injectAdContent(ad.content)
        } else {
          this.renderedAds.push({
            type: 'html',
            content: temp.innerHTML,
            scriptContent,
          })

          if (scriptContent) {
            this.injectProcessedScript(scriptContent, adIndex, 'queued')
          }
        }

        if (ad.tracking) {
          this.injectTrackingScript(ad.tracking)
        }

        if (hasMoreAds) {
          this.handleAdIndexProgress(false)
        } else if (this.renderedAds.length > 0) {
          logAdsEvent('[ADS] renderAdWithContent - all ads rendered, marking complete')
          this.markAdComplete()
        }
      },

      renderCurrentAd() {
        logAdsEvent('[ADS] renderCurrentAd - START', {
          currentAdIndex: this.currentAdIndex,
          totalAds: this.adsQueue.length,
          timestamp: new Date().toISOString(),
        })

        if (this.currentAdIndex >= this.adsQueue.length) {
          logAdsEvent('[ADS] renderCurrentAd - no more ads to try, showing broken ad')
          this.showBrokenAd()
          return
        }

        const ad = this.adsQueue[this.currentAdIndex]
        logAdsEvent('[ADS] renderCurrentAd - rendering ad', {
          adIndex: this.currentAdIndex,
          adId: ad.id,
          adType: ad.type,
        })

        try {
          if (ad.type === 'samba_ads' && ad.vastUrl) {
            logAdsEvent('[ADS] renderCurrentAd - rendering Samba ad')
            this.vastTagUrl = ad.vastUrl
            this.width = ad.width || '100%'
            this.height = ad.height || 'auto'
            this.hasError = false
            this.adKey++
            this.handleAdIndexProgress(true)
          } else if (
            (ad.type === 'text' || ad.type === 'embed' || ad.type === 'image') &&
            (ad.content || ad.html_content || ad.embed_url)
          ) {
            logAdsEvent('[ADS] renderCurrentAd - rendering text/embed/image ad', {
              adType: ad.type,
              hasContent: !!ad.content,
              hasHtmlContent: !!ad.html_content,
              hasEmbedUrl: !!ad.embed_url,
            })

            const htmlContent = ad.html_content || ad.content
            const embedUrl = ad.embed_url

            if (this.vastTagUrl) {
              logAdsEvent('[ADS] renderCurrentAd - Samba ad exists, setting as nonSambaContent')
              const embedContent = this.renderEmbedContent(htmlContent, embedUrl)
              this.nonSambaContent = embedContent
              this.injectTrackingScript(ad.tracking)
              this.handleAdIndexProgress(true)
            } else {
              const hasMoreAds = this.currentAdIndex + 1 < this.adsQueue.length
              const isFirstAd = !this.firstAdInjected && !this.renderedAds.length
              this.renderAdWithContent(ad, isFirstAd, hasMoreAds, htmlContent, embedUrl)
              if (isFirstAd) {
                this.firstAdInjected = true
              }
            }
          } else {
            logAdsEvent('[ADS] renderCurrentAd - ad missing required fields', {
              adId: ad.id,
              adType: ad.type,
              hasVastUrl: !!ad.vastUrl,
              hasContent: !!ad.content,
            })
            this.tryNextAd()
          }
        } catch (e) {
          logAdsEvent(
            '[ADS] renderCurrentAd - error rendering ad',
            {
              error: e.message,
              adId: ad.id,
              adType: ad.type,
            },
            'warn',
          )
          this.tryNextAd()
        }
      },

      tryNextAd() {
        logAdsEvent('[ADS] tryNextAd - moving to next ad', {
          currentAdIndex: this.currentAdIndex,
          totalAds: this.adsQueue.length,
        })

        this.currentAdIndex++
        if (this.currentAdIndex < this.adsQueue.length) {
          logAdsEvent('[ADS] tryNextAd - rendering next ad')
          this.renderCurrentAd()
        } else {
          logAdsEvent('[ADS] tryNextAd - no more ads, showing broken ad')
          this.showBrokenAd()
        }
      },

      injectAdContent(htmlString) {
        logAdsEvent('[ADS] injectAdContent - START', {
          hasHtmlString: !!htmlString,
          htmlStringLength: htmlString?.length,
          htmlStringPreview: htmlString?.substring(0, 200),
          timestamp: new Date().toISOString(),
        })

        // Check if content is empty
        if (!htmlString || htmlString.trim() === '') {
          logAdsEvent('[ADS] injectAdContent - empty content, showing broken ad', {
            htmlStringLength: htmlString?.length,
          })
          this.showBrokenAd()
          return
        }

        const temp = document.createElement('div')
        temp.innerHTML = htmlString

        const hasEmbed = this.hasEmbedContent(temp.innerHTML)

        logAdsEvent('[ADS] injectAdContent - checked for embeds', {
          hasEmbed,
          childrenCount: temp.children.length,
        })

        const script = temp.querySelector('script')

        if (script) {
          let scriptText = this.processAdScript(script.textContent)

          if (hasEmbed) {
            const trackingVarName = `tracked_embed_${this.position.replace(/[^a-zA-Z0-9]/g, '_')}`
            scriptText += `
            var ${trackingVarName} = false;

            function trackEmbed() {
              if (${trackingVarName}) {
                return;
              }

              if (typeof tracking === 'function') {
                tracking("${this.trackingId}");
                ${trackingVarName} = true;
              }
            }

            trackEmbed();

            if (typeof window.IntersectionObserver !== 'undefined') {
              const observer = new IntersectionObserver(function(entries) {
                entries.forEach(function(entry) {
                  if (entry.isIntersecting) {
                    trackEmbed();
                  }
                });
              });

              const element = document.getElementById("${this.bannerId}");
              if (element) {
                observer.observe(element);
              }
            }

            setTimeout(function() {
              trackEmbed();
            }, 1000);

            setTimeout(function() {
              trackEmbed();
            }, 3000);

            setTimeout(function() {
              trackEmbed();
            }, 5000);

            window.addEventListener('load', function() {
              trackEmbed();
            });

            // Embedly initialization handled by global setInterval
          `
          }

          this.scriptContent = scriptText
          script.remove()
        }

        this.adContent = temp.innerHTML

        logAdsEvent('[ADS] injectAdContent - set adContent', {
          hasAdContent: !!this.adContent,
          adContentLength: this.adContent.length,
          hasScriptContent: !!this.scriptContent,
          scriptContentLength: this.scriptContent?.length,
          hasEmbed,
          hasAdsQueue: this.adsQueue.length > 0,
          currentAdIndex: this.currentAdIndex,
          totalAds: this.adsQueue.length,
          hasMoreAds: this.currentAdIndex + 1 < this.adsQueue.length,
        })

        const isUsingQueue = this.adsQueue.length > 0
        const hasMoreAdsInQueue = isUsingQueue && this.currentAdIndex + 1 < this.adsQueue.length

        if (!hasMoreAdsInQueue) {
          if (isUsingQueue) {
            this.allAdsRendered = true
            if (this.vastTagUrl) {
              logAdsEvent(
                '[ADS] injectAdContent - no more ads in queue, but Samba ad exists - waiting for it to start',
                {
                  hasVastTagUrl: !!this.vastTagUrl,
                },
              )
            } else {
              logAdsEvent(
                '[ADS] injectAdContent - no more ads in queue (no Samba), emitting ad-loaded',
                {},
              )
              this.markAdComplete()
            }
          } else {
            if (this.vastTagUrl && !this.adStarted) {
              logAdsEvent(
                '[ADS] injectAdContent - legacy path with Samba ad, waiting for it to start',
                {
                  hasVastTagUrl: !!this.vastTagUrl,
                  adStarted: this.adStarted,
                },
              )
            } else {
              logAdsEvent(
                '[ADS] injectAdContent - legacy path (no Samba or Samba started), emitting ad-loaded',
                {
                  hasVastTagUrl: !!this.vastTagUrl,
                  adStarted: this.adStarted,
                },
              )
              this.markAdComplete()
            }
          }
        } else {
          logAdsEvent(
            '[ADS] injectAdContent - more ads in queue, skipping markAdComplete for now',
            {
              nextAdIndex: this.currentAdIndex + 1,
              totalAds: this.adsQueue.length,
            },
          )
        }

        // Schedule tracking injection asynchronously (won't block event emission)
        this.$nextTick(() => {
          logAdsEvent('[ADS] injectAdContent - nextTick, calling injectScriptForTracking', {})
          this.injectScriptForTracking()
        })

        logAdsEvent('[ADS] injectAdContent - END', {
          hasAdContent: !!this.adContent,
          hasScriptContent: !!this.scriptContent,
        })
      },

      async injectScript() {
        logAdsEvent('[ADS] injectScript - START', {
          hasScriptContent: !!this.scriptContent,
          scriptContentLength: this.scriptContent?.length,
          isScriptInjected: this.isScriptInjected,
          vastTagUrl: this.vastTagUrl,
          hasVastTagUrl: !!this.vastTagUrl,
          hasScriptContainer: !!this.$refs.scriptContainer,
          timestamp: new Date().toISOString(),
        })

        if (!this.scriptContent || this.isScriptInjected) {
          logAdsEvent('[ADS] injectScript - early return', {
            hasScriptContent: !!this.scriptContent,
            isScriptInjected: this.isScriptInjected,
            hasAdContent: !!this.adContent,
            adContentLength: this.adContent?.length,
          })

          // If no script and no meaningful content, mark as complete
          if (
            !this.scriptContent &&
            (!this.adContent ||
              this.adContent.trim() === '' ||
              this.adContent.includes('broken-ad-container'))
          ) {
            logAdsEvent('[ADS] injectScript - no script and no content, marking complete', {
              hasAdContent: !!this.adContent,
              isBrokenAd: this.adContent?.includes('broken-ad-container'),
            })
            this.markAdComplete()
          }
          return
        }

        // 🔥 CRITICAL FIX: injectScript() now only handles Samba ads
        // Non-Samba ad script injection moved to injectScriptForTracking()
        if (!this.vastTagUrl) {
          logAdsEvent(
            '[ADS] injectScript - called for non-Samba ad, should use injectScriptForTracking() instead',
            {},
            'warn',
          )
          return
        }

        logAdsEvent('[ADS] injectScript - Samba ad script injection', {
          vastTagUrl: this.vastTagUrl,
          hasScriptContainer: !!this.$refs.scriptContainer,
        })
        try {
          if (this.$refs.scriptContainer) {
            const scriptElement = document.createElement('script')
            scriptElement.textContent = this.scriptContent
            this.$refs.scriptContainer.appendChild(scriptElement)
            this.isScriptInjected = true
            logAdsEvent('[ADS] injectScript - Samba script injected successfully', {
              isScriptInjected: this.isScriptInjected,
            })
          } else {
            logAdsEvent('[ADS] injectScript - no script container ref', {})
          }
        } catch (error) {
          logAdsEvent('[ADS] injectScript - ERROR injecting Samba script', {
            error: error.message,
            errorName: error.name,
            errorStack: error.stack,
            vastTagUrl: this.vastTagUrl,
            hasScriptContent: !!this.scriptContent,
          })
        }

        logAdsEvent('[ADS] injectScript - END', {
          isScriptInjected: this.isScriptInjected,
        })
      },

      async injectScriptForTracking() {
        logAdsEvent('[ADS] injectScriptForTracking - START', {
          hasScriptContent: !!this.scriptContent,
          scriptContentLength: this.scriptContent?.length,
          firstAdScriptInjected: this.firstAdScriptInjected,
          hasScriptContainer: !!this.$refs.scriptContainer,
          timestamp: new Date().toISOString(),
        })

        if (!this.scriptContent || this.firstAdScriptInjected) {
          logAdsEvent('[ADS] injectScriptForTracking - early return', {
            hasScriptContent: !!this.scriptContent,
            firstAdScriptInjected: this.firstAdScriptInjected,
          })
          return
        }

        // Samba ads shouldn't reach here (they don't call injectAdContent)
        if (this.vastTagUrl) {
          logAdsEvent('[ADS] injectScriptForTracking - unexpected Samba ad path', {}, 'warn')
          return
        }

        logAdsEvent('[ADS] injectScriptForTracking - waiting for elements', {
          bannerId: this.bannerId,
          trackingId: this.trackingId,
        })

        try {
          await Promise.all([
            this.waitForElement(this.bannerId),
            this.waitForElement(this.trackingId),
          ])

          logAdsEvent('[ADS] injectScriptForTracking - elements ready, injecting script', {
            bannerId: this.bannerId,
            trackingId: this.trackingId,
            hasScriptContainer: !!this.$refs.scriptContainer,
          })

          if (this.$refs.scriptContainer) {
            const scriptElement = document.createElement('script')
            scriptElement.textContent = this.scriptContent
            this.$refs.scriptContainer.appendChild(scriptElement)
            this.firstAdScriptInjected = true

            logAdsEvent('[ADS] injectScriptForTracking - script injected', {
              firstAdScriptInjected: this.firstAdScriptInjected,
            })

            const hasEmbed = this.hasEmbedContent(this.adContent)

            if (hasEmbed) {
              logAdsEvent('[ADS] injectScriptForTracking - triggering embed tracking')
              this.triggerEmbedTracking()
            } else {
              logAdsEvent('[ADS] injectScriptForTracking - triggering regular tracking')
              this.triggerRegularTracking()
            }
          } else {
            logAdsEvent('[ADS] injectScriptForTracking - no script container ref', {}, 'warn')
          }
        } catch (error) {
          logAdsEvent(
            '[ADS] injectScriptForTracking - ERROR',
            {
              error: error.message,
              errorName: error.name,
              errorStack: error.stack,
              bannerId: this.bannerId,
              trackingId: this.trackingId,
              hasScriptContent: !!this.scriptContent,
            },
            'warn',
          )
          // Error in tracking shouldn't affect ad display (already emitted ad-loaded)
        }

        logAdsEvent('[ADS] injectScriptForTracking - END')
      },

      waitForElement(elementId, maxAttempts = 50) {
        return new Promise((resolve, reject) => {
          let attempts = 0
          const check = () => {
            attempts++
            const element = document.getElementById(elementId)
            if (element && element.offsetParent !== null) {
              resolve(element)
            } else if (attempts >= maxAttempts) {
              reject(new Error(`Element ${elementId} not found after ${maxAttempts} attempts`))
            } else {
              setTimeout(check, 100)
            }
          }
          check()
        })
      },

      parseSambaAdParameters(htmlString) {
        logAdsEvent('[ADS] parseSambaAdParameters - START', {
          hasHtmlString: !!htmlString,
          htmlStringLength: htmlString?.length,
          htmlStringPreview: htmlString?.substring(0, 200),
        })

        try {
          const tempDiv = document.createElement('div')
          tempDiv.innerHTML = htmlString

          logAdsEvent('[ADS] parseSambaAdParameters - created temp div', {
            childrenCount: tempDiv.children.length,
          })

          const sambaElement = tempDiv.querySelector(
            '[data-samba-vast], .samba-vast-player, .samba-vast',
          )

          logAdsEvent('[ADS] parseSambaAdParameters - searched for Samba element', {
            foundSambaElement: !!sambaElement,
            elementTag: sambaElement?.tagName,
            elementClasses: sambaElement?.className,
          })

          if (!sambaElement) {
            logAdsEvent('[ADS] parseSambaAdParameters - no Samba element found', {})
            return {
              success: false,
              isSambaAd: false,
            }
          }

          const vastTagUrl =
            sambaElement.getAttribute('data-samba-vast') ||
            sambaElement.getAttribute('data-vast-tag-url') ||
            sambaElement.getAttribute('vast-tag-url') ||
            sambaElement.dataset.sambaVast ||
            sambaElement.dataset.vastTagUrl

          logAdsEvent('[ADS] parseSambaAdParameters - extracted vastTagUrl', {
            hasVastTagUrl: !!vastTagUrl,
            vastTagUrl,
            dataSambaVast: sambaElement.getAttribute('data-samba-vast'),
            dataVastTagUrl: sambaElement.getAttribute('data-vast-tag-url'),
          })

          if (!vastTagUrl) {
            logAdsEvent('[ADS] parseSambaAdParameters - no vastTagUrl found', {})
            return {
              success: false,
              isSambaAd: false,
            }
          }

          const width =
            sambaElement.getAttribute('data-width') ||
            sambaElement.getAttribute('width') ||
            sambaElement.dataset.width ||
            '100%'

          const height =
            sambaElement.getAttribute('data-height') ||
            sambaElement.getAttribute('height') ||
            sambaElement.dataset.height ||
            'auto'

          logAdsEvent('[ADS] parseSambaAdParameters - extracted dimensions', {
            width,
            height,
          })

          let nonSambaContent = ''
          const allElements = Array.from(tempDiv.children)
          allElements.forEach((element) => {
            if (element !== sambaElement) {
              nonSambaContent += element.outerHTML
            }
          })

          logAdsEvent('[ADS] parseSambaAdParameters - extracted non-Samba content', {
            hasNonSambaContent: !!nonSambaContent,
            nonSambaContentLength: nonSambaContent.length,
            allElementsCount: allElements.length,
          })

          const scriptElement = tempDiv.querySelector('script')
          let scriptContent = null
          if (scriptElement) {
            scriptContent = scriptElement.textContent
          }

          logAdsEvent('[ADS] parseSambaAdParameters - extracted script', {
            hasScriptElement: !!scriptElement,
            hasScriptContent: !!scriptContent,
            scriptContentLength: scriptContent?.length,
          })

          const result = {
            success: true,
            isSambaAd: true,
            vastTagUrl,
            width,
            height,
            nonSambaContent: nonSambaContent.trim() || null,
            scriptContent,
          }

          logAdsEvent('[ADS] parseSambaAdParameters - SUCCESS', {
            success: result.success,
            isSambaAd: result.isSambaAd,
            vastTagUrl: result.vastTagUrl,
            width: result.width,
            height: result.height,
            hasNonSambaContent: !!result.nonSambaContent,
            hasScriptContent: !!result.scriptContent,
          })

          return result
        } catch (error) {
          logAdsEvent('[ADS] parseSambaAdParameters - ERROR', {
            error: error.message,
            errorName: error.name,
            errorStack: error.stack,
            errorString: String(error),
            hasHtmlString: !!htmlString,
            htmlStringLength: htmlString?.length,
          })
          return {
            success: false,
            error: error.message,
            isSambaAd: false,
          }
        }
      },

      handleSambaAd(htmlString) {
        logAdsEvent('[ADS] handleSambaAd - START', {
          hasHtmlString: !!htmlString,
          htmlStringLength: htmlString?.length,
          previousVastTagUrl: this.vastTagUrl,
          previousHasError: this.hasError,
          timestamp: new Date().toISOString(),
        })

        const result = this.parseSambaAdParameters(htmlString)

        logAdsEvent('[ADS] handleSambaAd - parse result', {
          success: result.success,
          isSambaAd: result.isSambaAd,
          hasVastTagUrl: !!result.vastTagUrl,
          vastTagUrl: result.vastTagUrl,
          width: result.width,
          height: result.height,
          hasNonSambaContent: !!result.nonSambaContent,
          hasScriptContent: !!result.scriptContent,
          hasError: !!result.error,
          error: result.error,
        })

        if (!result.success || !result.isSambaAd) {
          logAdsEvent('[ADS] handleSambaAd - not a Samba ad, injecting as regular content', {
            success: result.success,
            isSambaAd: result.isSambaAd,
          })
          this.injectAdContent(htmlString)
          return
        }

        logAdsEvent('[ADS] handleSambaAd - setting Samba ad properties', {
          vastTagUrl: result.vastTagUrl,
          width: result.width,
          height: result.height,
          previousVastTagUrl: this.vastTagUrl,
        })
        this.vastTagUrl = result.vastTagUrl
        this.width = result.width
        this.height = result.height

        logAdsEvent('[ADS] handleSambaAd - setting SAMBA_AD_SHOWN_ON_PAGE flag', {
          previousValue: window.SAMBA_AD_SHOWN_ON_PAGE,
        })
        window.SAMBA_AD_SHOWN_ON_PAGE = true

        if (result.nonSambaContent) {
          logAdsEvent('[ADS] handleSambaAd - setting non-Samba content', {
            nonSambaContentLength: result.nonSambaContent.length,
          })
          this.nonSambaContent = result.nonSambaContent
        }

        if (result.scriptContent) {
          logAdsEvent('[ADS] handleSambaAd - setting script content, will inject on nextTick', {
            scriptContentLength: result.scriptContent.length,
          })
          this.scriptContent = result.scriptContent
          this.$nextTick(() => {
            logAdsEvent('[ADS] handleSambaAd - nextTick, injecting script', {})
            this.injectScript()
          })
        }

        logAdsEvent('[ADS] handleSambaAd - END', {
          vastTagUrl: this.vastTagUrl,
          width: this.width,
          height: this.height,
          hasNonSambaContent: !!this.nonSambaContent,
          hasScriptContent: !!this.scriptContent,
          sambaAdShown: window.SAMBA_AD_SHOWN_ON_PAGE,
        })
      },

      showBrokenAd() {
        logAdsEvent('[ADS] showBrokenAd - START', {
          zoneId: this.zoneId,
          position: this.position,
          type: this.type,
          currentRoute: this.$route?.path,
        })

        // For interstitial ads, handle gracefully - don't immediately mark as broken
        // Let the interstitial page decide if this is a total failure
        // Allow other ads in the zone to load
        if (this.type === 'interstitial') {
          logAdsEvent('[ADS] showBrokenAd - interstitial ad broken, checking if more ads to try', {
            hasAdsQueue: !!this.adsQueue.length,
            currentAdIndex: this.currentAdIndex,
            totalAds: this.adsQueue.length,
            allAdsRendered: this.allAdsRendered,
          })

          if (this.adsQueue.length > 0 && this.currentAdIndex < this.adsQueue.length) {
            logAdsEvent('[ADS] showBrokenAd - more ads in queue, trying next ad')
            this.tryNextAd()
            return
          }

          logAdsEvent(
            '[ADS] showBrokenAd - no more ads to try, emitting ad-error and marking complete as broken',
          )
          this.$emit('ad-error', {
            zoneId: this.zoneId,
            position: this.position,
            error: 'Ad failed to load',
          })

          this.isBrokenAd = true
          this.isLoadingAd = false
          this.markAdComplete()
          return
        }

        const routePath = this.$route?.path || window.location.pathname || ''

        const clearBrokenAdTimers = () => {
          if (this.brokenAdTimeout) {
            clearTimeout(this.brokenAdTimeout)
            this.brokenAdTimeout = null
          }
          if (this.brokenAdCountdownInterval) {
            clearInterval(this.brokenAdCountdownInterval)
            this.brokenAdCountdownInterval = null
          }
        }

        let markAdCompleteTriggered = false
        const markAdCompleteSafely = (context) => {
          if (markAdCompleteTriggered) {
            return
          }
          markAdCompleteTriggered = true
          try {
            this.markAdComplete()
          } catch (error) {
            logAdsEvent('[ADS] showBrokenAd - ERROR calling markAdComplete', {
              context,
              error: error.message,
              errorStack: error.stack,
            })
          }
        }

        this.hasError = false
        this.isLoadingAd = false
        this.isBrokenAd = true // Mark ad as broken (only for non-interstitial)

        logAdsEvent('[ADS] showBrokenAd - context', {
          routePath: this.$route?.path,
          isBrokenAd: this.isBrokenAd,
        }).catch(() => {})

        clearBrokenAdTimers()
        this.adContent = ''
        this.nonSambaContent = ''

        const scheduleImmediateCompletion = (context) => {
          this.$nextTick(() => {
            markAdCompleteSafely(`${context}-next-tick`)
          })

          setTimeout(() => {
            markAdCompleteSafely(`${context}-fallback`)
          }, 500)
        }

        logAdsEvent('[ADS] showBrokenAd - redirecting immediately', {
          routePath,
        }).catch(() => {})
        scheduleImmediateCompletion('default')

        setTimeout(() => {
          logAdsEvent('[ADS] showBrokenAd - safety fallback check', {
            routePath,
          }).catch(() => {})
          markAdCompleteSafely('safety-fallback')
        }, 2000)

        logAdsEvent('[ADS] showBrokenAd - END', {
          hasAdContent: !!this.adContent,
          adContentLength: this.adContent?.length,
        })
      },

      onSambaAdStarted() {
        logAdsEvent('[ADS] onSambaAdStarted - START', {
          zoneId: this.zoneId,
          position: this.position,
          vastTagUrl: this.vastTagUrl,
          allAdsRendered: this.allAdsRendered,
          timestamp: new Date().toISOString(),
        })

        this.$emit('ad-started', {
          zoneId: this.zoneId,
          position: this.position,
          isSambaAd: true,
        })

        if (this.allAdsRendered) {
          logAdsEvent(
            '[ADS] onSambaAdStarted - all ads rendered and Samba ad started, marking complete',
          )
          this.markAdComplete()
        } else {
          logAdsEvent('[ADS] onSambaAdStarted - waiting for more ads to render', {
            allAdsRendered: this.allAdsRendered,
            currentAdIndex: this.currentAdIndex,
            totalAds: this.adsQueue.length,
          })
        }

        logAdsEvent('[ADS] onSambaAdStarted - END', {
          zoneId: this.zoneId,
          position: this.position,
        })
      },

      onSambaAdCompleted() {
        logAdsEvent('[ADS] onSambaAdCompleted - START', {
          zoneId: this.zoneId,
          position: this.position,
          vastTagUrl: this.vastTagUrl,
          hasError: this.hasError,
          isLoadingAd: this.isLoadingAd,
          timestamp: new Date().toISOString(),
        })

        this.$emit('ad-completed', {
          zoneId: this.zoneId,
          position: this.position,
        })

        logAdsEvent('[ADS] onSambaAdCompleted - marking ad complete', {})
        this.markAdComplete()

        logAdsEvent('[ADS] onSambaAdCompleted - END', {
          zoneId: this.zoneId,
          position: this.position,
        })
      },

      onSambaAdImpression() {
        logAdsEvent('[ADS] onSambaAdImpression - START', {
          zoneId: this.zoneId,
          position: this.position,
          trackingSent: this.trackingSent,
          vastTagUrl: this.vastTagUrl,
          timestamp: new Date().toISOString(),
        })

        if (!this.trackingSent) {
          logAdsEvent('[ADS] onSambaAdImpression - sending manual tracking', {
            trackingSent: this.trackingSent,
          })
          this.sendManualTracking()
        } else {
          logAdsEvent('[ADS] onSambaAdImpression - tracking already sent, skipping', {
            trackingSent: this.trackingSent,
          })
        }

        logAdsEvent('[ADS] onSambaAdImpression - END', {
          trackingSent: this.trackingSent,
        })
      },

      onSambaAdError(error) {
        logAdsEvent('[ADS] onSambaAdError - START', {
          error,
          errorMessage: error?.message,
          errorString: error?.toString(),
          errorType: typeof error,
          zoneId: this.zoneId,
          position: this.position,
          isLoadingAd: this.isLoadingAd,
          hasError: this.hasError,
          vastTagUrl: this.vastTagUrl,
          allAdsRendered: this.allAdsRendered,
          hasNonSambaContent: !!this.nonSambaContent,
          hasAdContent: !!this.adContent,
          timestamp: new Date().toISOString(),
        })

        if (this.isLoadingAd || this.hasError) {
          logAdsEvent('[ADS] onSambaAdError - already loading or has error, returning', {
            isLoadingAd: this.isLoadingAd,
            hasError: this.hasError,
          })
          return
        }

        if (this.allAdsRendered && (this.nonSambaContent || this.adContent)) {
          logAdsEvent(
            '[ADS] onSambaAdError - all ads rendered with valid content, marking complete',
            {
              hasNonSambaContent: !!this.nonSambaContent,
              hasAdContent: !!this.adContent,
            },
          )
          this.vastTagUrl = ''
          this.markAdComplete()
          return
        }

        const errorMessage = error?.message || error?.toString() || ''
        logAdsEvent('[ADS] onSambaAdError - handling error', {
          errorMessage,
        })
        this.handleAdError(errorMessage)

        logAdsEvent('[ADS] onSambaAdError - END', {
          hasError: this.hasError,
          isLoadingAd: this.isLoadingAd,
        })
      },

      onSambaAdSkipped() {
        logAdsEvent('[ADS] onSambaAdSkipped - START', {
          zoneId: this.zoneId,
          position: this.position,
          vastTagUrl: this.vastTagUrl,
          timestamp: new Date().toISOString(),
        })

        this.$emit('ad-skipped', {
          zoneId: this.zoneId,
          position: this.position,
        })

        logAdsEvent('[ADS] onSambaAdSkipped - marking ad complete', {})
        this.markAdComplete()

        logAdsEvent('[ADS] onSambaAdSkipped - END', {
          zoneId: this.zoneId,
          position: this.position,
        })
      },
    },
  }
</script>

<style>
  .ads {
    width: 100%;
    position: relative;
    display: flex;
    margin: 10px 0;
    justify-content: center;
    align-items: center;
    overflow: hidden;
    z-index: 999;
  }
  .ads .placeholder {
    height: auto;
    width: 100%;
    max-width: min(600px, 100vw);
    background: rgba(140, 126, 126, 0);
    position: relative;
    margin: 0 auto;
    padding: 0 15px;
    border-radius: 3px;
    display: flex;
    justify-content: center;
    align-items: center;
    box-sizing: border-box;
  }
  .ad-container {
    width: 100%;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    max-width: 600px;
    gap: 10px;
    margin: 0 auto;
  }
  .ad-container img {
    max-width: 100%;
    height: auto;
    display: block;
    margin: 0 auto;
  }
  /* Handle embedded content like iframes */
  .ad-container iframe {
    max-width: 100%;
    width: 100%;
    height: auto;
    min-height: 400px;
  }
  /* Handle Embedly responsive content - matching backend structure */
  .ad-container .embedly-responsive {
    position: relative;
    width: 100%;
    max-width: 100%;
    height: auto;
    min-height: 400px;
    overflow: hidden;
    clear: both;
    background: transparent;
  }

  .ad-container .embedly-responsive .embedly-embed,
  .ad-container .embedly-responsive .embedly-card {
    position: relative;
    width: 100%;
    height: auto;
    max-width: 100%;
  }

  .ad-container .embedly-responsive iframe {
    max-width: 100%;
    width: 100%;
    height: auto;
    min-height: 400px;
  }

  .ad-container .embedly-card {
    max-width: 100% !important;
    width: 100% !important;
    height: auto !important;
  }

  /* Container div styling matching backend - banner_{adId}_{position} */
  .ad-container > div[id^='banner_'] {
    clear: both;
    background: transparent;
    width: 100%;
    max-width: 100%;
    height: auto;
    min-height: 400px;
  }

  /* Samba ad styles */
  .samba-ad-wrapper {
    width: 100%;
    max-width: min(100%, 100vw);
    overflow: hidden;
    box-sizing: border-box;
    position: relative;
    background: transparent !important;
    border-radius: 4px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
  }

  .samba-ad-wrapper * {
    background: transparent !important;
    background-color: transparent !important;
  }

  @media (max-width: 768px) {
    .samba-ad-wrapper {
      max-height: 75vh;
      background: transparent;
    }
  }

  @media (max-width: 480px) {
    .samba-ad-wrapper {
      max-height: 65vh;
      background: transparent;
    }
  }

  .player-state-overlay {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    z-index: 1000;
    border-radius: 4px;
  }

  .player-state-overlay.loading-state {
    background: rgba(0, 0, 0, 0.9);
  }

  .player-state-overlay.error-state {
    background: rgba(0, 0, 0, 0.8);
  }

  .player-state-overlay .loading-spinner {
    width: 50px;
    height: 50px;
    border: 4px solid rgba(255, 255, 255, 0.3);
    border-top-color: #fff;
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }

  .player-state-overlay .loading-text {
    color: #fff;
    margin-top: 15px;
    font-size: 14px;
    font-family: Arial, sans-serif;
  }

  .player-state-overlay.error-state svg {
    opacity: 0.9;
  }

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }

  .samba-vast-player {
    border-radius: 3px solid #000;
    width: 100%;
    max-width: min(100%, 100vw);
    overflow: hidden;
    box-sizing: border-box;
    position: relative;
    transition: opacity 0.2s ease;
  }

  .samba-vast-player.player-hidden {
    opacity: 0;
    visibility: hidden;
    pointer-events: none;
    /* Keep in document flow to prevent layout shift */
  }

  .non-samba-ad-wrapper {
    width: 100%;
    max-width: 100%;
  }

  .rendered-ad-wrapper {
    width: 100%;
    max-width: 100%;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: 10px;
    margin-top: 10px;
  }

  .rendered-ad-wrapper .ad-content {
    width: 100%;
    max-width: 100%;
  }

  .rendered-ad-wrapper .ad-content img,
  .rendered-ad-wrapper .ad-content iframe {
    max-width: 100%;
    height: auto;
    display: block;
    margin: 0 auto;
  }

  .samba-ad-wrapper-queue {
    width: 100%;
    max-width: min(100%, 100vw);
    overflow: hidden;
    box-sizing: border-box;
    position: relative;
    background: transparent !important;
    border-radius: 4px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    margin-top: 10px;
  }
</style>
